import React, { useEffect, useState } from 'react';
import { Badge, Btn, Field, Grid, Note, Textarea, TextInput, Toggle } from './ui';

/**
 * Section 8 - the global compliance footer.
 *
 * This is the section that carries a legal claim, so the badge beside it is stated against what is
 * actually stored rather than shown as decoration. All five fields now save on `EmailSettings` and
 * render in the footer of every template.
 *
 * The one that matters is the **physical mailing address**. CAN-SPAM requires it in commercial
 * email, and until it is set the backend refuses to schedule or send a campaign — deliberately not
 * defaulted, because a seeded placeholder would satisfy that check and ship a false address to
 * every inbox. Account and billing email is never gated on it, so nobody is locked out of their
 * account by an empty field here.
 *
 * The two link texts change wording only. The links themselves are generated per recipient with a
 * signed token, and an empty wording falls back to the built-in label rather than rendering an
 * anchor nobody can see.
 */

const ComplianceFooter = ({ policy, onSave, busy, saving }) => {
  const [form, setForm] = useState({});

  /*
    Seeded from the policy, and re-seeded after each save.

    Kept as one object rather than five useStates so `dirty` is a single comparison — five separate
    flags is five chances for the Save button to disagree with the form.
  */
  useEffect(() => {
    setForm({
      complianceFooterText: policy?.complianceFooterText || '',
      postalAddress: policy?.postalAddress || '',
      unsubscribeLinkText: policy?.unsubscribeLinkText || '',
      viewInBrowserLinkText: policy?.viewInBrowserLinkText || '',
      sponsorDisclosureText: policy?.sponsorDisclosureText || ''
    });
  }, [policy]);

  const set = (key) => (event) => setForm({ ...form, [key]: event.target.value });

  const stored = (key) => policy?.[key] || '';
  const dirty = Object.keys(form).some((key) => form[key] !== stored(key));

  /*
    The address is the only field that cannot be saved empty.

    Clearing it stops every campaign, which is a deliberate act and should not be reachable by
    tabbing through a form and submitting a blank box. The backend refuses an empty string for the
    same reason; this only avoids a pointless round trip.
  */
  const save = () => {
    const payload = { ...form };
    if (!payload.postalAddress.trim()) delete payload.postalAddress;
    onSave(payload);
  };

  const addressMissing = !policy?.postalAddressRequirementMet;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <Grid cols="1fr 1fr" gap="24px">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
          <Field
            label="Physical Mailing Address"
            hint="CAN-SPAM requires a postal address in commercial email. Until this is set, campaigns cannot be scheduled or sent. Account and billing email is unaffected."
          >
            <TextInput
              value={form.postalAddress || ''}
              onChange={set('postalAddress')}
              placeholder="Playaz4Playaz Arena Inc., 100 Arena Way, Suite 400, Los Angeles, CA 90015"
            />
          </Field>

          {addressMissing ? (
            <Note>
              No address is stored, so campaigns are currently refused. Verification, password-reset
              and billing email continue to send normally.
            </Note>
          ) : null}

          <Grid cols="1fr 1fr" gap="12px">
            <Field
              label="Unsubscribe Link Text"
              hint="Wording only. The link is generated per recipient with a signed token and is always present on marketing email. Leave empty to use the default."
            >
              <TextInput
                value={form.unsubscribeLinkText || ''}
                onChange={set('unsubscribeLinkText')}
                placeholder="Unsubscribe"
              />
            </Field>
            <Field label="View in Browser Link Text">
              <TextInput
                value={form.viewInBrowserLinkText || ''}
                onChange={set('viewInBrowserLinkText')}
                placeholder="View in browser"
              />
            </Field>
          </Grid>

          <Toggle
            on
            disabled
            onChange={() => {}}
            label="Force apply compliant footer to all outgoing templates"
          />
          <Note>
            Always on, and not a setting. Every template composes its footer from the same
            component, so there is no path by which an email leaves without one - which is a
            stronger guarantee than a switch that could be turned off.
          </Note>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
          <Field
            label="'You received this email because…' Explanation Statement"
            hint="Rendered in the footer of every email, alongside the unsubscribe link."
          >
            <Textarea
              rows={4}
              minHeight="112px"
              value={form.complianceFooterText || ''}
              placeholder="You received this email because you registered an account on Playaz4Playaz or chose to follow custom fan alerts."
              onChange={set('complianceFooterText')}
            />
          </Field>

          <Field
            label="Sponsor Disclosure & Partnership Text (Optional)"
            hint="Left empty, the row is removed from the footer entirely rather than rendering a blank line."
          >
            <Textarea
              rows={3}
              minHeight="92px"
              value={form.sponsorDisclosureText || ''}
              onChange={set('sponsorDisclosureText')}
            />
          </Field>

          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <Btn onClick={save} disabled={busy || !dirty}>
              {saving ? 'Saving…' : 'Save Compliance Footer'}
            </Btn>
            <Btn
              variant="outline"
              onClick={() =>
                setForm({
                  complianceFooterText: stored('complianceFooterText'),
                  postalAddress: stored('postalAddress'),
                  unsubscribeLinkText: stored('unsubscribeLinkText'),
                  viewInBrowserLinkText: stored('viewInBrowserLinkText'),
                  sponsorDisclosureText: stored('sponsorDisclosureText')
                })
              }
              disabled={!dirty}
            >
              Discard
            </Btn>
            {dirty ? <Badge tone="amber">Unsaved</Badge> : null}
          </div>

          <Note>
            Saving rewrites the footer for every future send immediately. Emails already in an inbox
            are unchanged — nothing rebuilds what has been delivered.
          </Note>
        </div>
      </Grid>
    </div>
  );
};

/**
 * The badge beside the section title.
 *
 * Driven by `postalAddressRequirementMet`, which the backend derives from what is actually stored.
 * The design's green "CAN-SPAM Compliant" badge asserted a claim nobody had checked; this one
 * reports a fact.
 */
export const ComplianceBadge = ({ policy }) => {
  const compliant = !!policy?.postalAddressRequirementMet && !!policy?.complianceFooterText;

  return (
    <Badge
      tone={compliant ? 'green' : 'amber'}
      title={
        compliant
          ? 'Postal address, explanation statement and per-recipient unsubscribe link are present on every marketing email.'
          : 'A postal address and an explanation statement are both required before a campaign can send.'
      }
    >
      {compliant ? 'CAN-SPAM requirements met' : 'Postal address or statement missing'}
    </Badge>
  );
};

export default ComplianceFooter;
